import { Injectable } from '@angular/core';

import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { ConfigurationService } from '@sondermind/configuration';
import { HttpService } from '@sondermind/http';
import {
  ClientInsurance,
  ClientInsuranceCreateAttributes,
  IClientInsuranceResponse,
} from '@sondermind/utilities/models-client-insurance';
import {
  IAPIFetchRequestOptions,
  IAPIFetchResponse,
  IAPIListRequestOptions,
  IAPIListResponse,
} from '@sondermind/utilities/models-http';

import {
  IBenefitsInformation,
  ITempImprovementPatientInsurances,
  convertLastBenefitInformation,
  convertTempImprovedPatientInsurance,
} from '../models/temp-improvement-patient-insurances.interface';

export type CashPayId = { persona_client_id: number; } | { patient_id: number; };

@Injectable({
  providedIn: 'root',
})
export class ClientInsuranceHttpService {
  constructor(
    private configurationService: ConfigurationService,
    private http: HttpService
  ) { }

  listTempImprovement(opts: Partial<IAPIListRequestOptions> = {}): Observable<ITempImprovementPatientInsurances[]> {
    const sanitized = this.sanitizePersonaClientIdFilter(opts);
    if (!sanitized) {
      return of([]);
    }
    const params = this.http.generateListParams(sanitized);

    return this.http.get<IAPIListResponse<ITempImprovementPatientInsurances>>(this.indexTempImpUrl, params)
      .pipe(
        map((resp) => resp.data.map((ins) => convertTempImprovedPatientInsurance(ins))),
        this.http.handleHttpError(opts.error)
      );
  }

  listTempImprovementRaw$(
    opts: Partial<IAPIListRequestOptions> = {}
  ): Observable<IAPIListResponse<ITempImprovementPatientInsurances>> {
    const sanitized = this.sanitizePersonaClientIdFilter(opts);
    if (!sanitized) {
      return of({ data: [] } as IAPIListResponse<ITempImprovementPatientInsurances>);
    }
    const params = this.http.generateListParams(sanitized);

    return this.http.get<IAPIListResponse<any>>(this.indexTempImpUrl, params)
      .pipe(
        map((resp) => ({
          ...resp,
          data: resp.data.map((ins) => convertTempImprovedPatientInsurance(ins)),
        })),
        this.http.handleHttpError(opts.error)
      );
  }

  fetchTempImp$(
    id: number,
    opts: Partial<IAPIListRequestOptions> = {}
  ): Observable<ITempImprovementPatientInsurances> {
    const params = this.http.generateListParams(opts);

    return this.http.get<IAPIFetchResponse<any>>(this.fetchTempImpUrl(id), params)
      .pipe(
        map((res) => convertTempImprovedPatientInsurance(res.data)),
        this.http.handleHttpError(opts.error)
      );
  }

  create(
    body: ClientInsuranceCreateAttributes,
    accountSetupContext: string = null,
    opts: Partial<IAPIFetchRequestOptions> = {}
  ): Observable<ClientInsurance> {
    const params = this.http.generateFetchParams(opts);

    return this.http.post<IAPIFetchResponse<IClientInsuranceResponse>>(
      this.indexTempImpUrl,
      { patient_insurance: body, account_setup_context: accountSetupContext },
      params
    )
      .pipe(
        map((resp) => new ClientInsurance(resp.data)),
        this.http.handleHttpError(opts.error)
      );
  }

  update(
    id: number | string,
    body: ClientInsuranceCreateAttributes,
    accountSetupContext: string = null,
    opts: Partial<IAPIFetchRequestOptions> = {}
  ): Observable<ClientInsurance> {
    const params = this.http.generateFetchParams(opts);

    return this.http.patch<IAPIFetchResponse<IClientInsuranceResponse>>(
      this.fetchTempImpUrl(id),
      { patient_insurance: body, account_setup_context: accountSetupContext },
      params
    ).pipe(
      map((resp) => new ClientInsurance(resp.data)),
      this.http.handleHttpError(opts.error)
    );
  }

  addCashPay(
    cashPayId?: CashPayId,
    opts: Partial<IAPIFetchRequestOptions> = {}
  ): Observable<ITempImprovementPatientInsurances[]> {
    const params = this.http.generateFetchParams(opts);

    return this.http.post<IAPIListResponse<ITempImprovementPatientInsurances>>(
      `${this.indexTempImpUrl}/add_cash_pay`,
      cashPayId,
      params
    )
      .pipe(
        map((resp) => resp.data.map((ins) => convertTempImprovedPatientInsurance(ins))),
        this.http.handleHttpError(opts.error)
      );
  }

  addProBono(
    patient_id: number,
    add_redemptions: number,
    opts: Partial<IAPIFetchRequestOptions> = {}
  ): Observable<ITempImprovementPatientInsurances> {
    const params = this.http.generateServiceParams(opts);

    return this.http.post<IAPIFetchResponse<ITempImprovementPatientInsurances>>(
      `${this.indexTempImpUrl}/add_pro_bono`,
      { patient_id, add_redemptions },
      params
    )
      .pipe(
        map((resp) => convertTempImprovedPatientInsurance(resp.data)),
        this.http.handleHttpError(opts.error)
      );
  }

  delete(
    id: number | string,
    opts: Partial<IAPIFetchRequestOptions> = {}
  ): Observable<null> {
    const params = this.http.generateFetchParams(opts);

    return this.http.delete<null>(this.fetchTempImpUrl(id), params)
      .pipe(this.http.handleHttpError(opts.error));
  }

  deleteMultiple(
    ids: number[],
    opts: Partial<IAPIFetchRequestOptions> = {}
  ): Observable<null> {
    const params = this.http.generateFetchParams(opts);

    return this.http.post<null>(`${this.indexTempImpUrl}/bulk_archive`, { patient_insurance_ids: ids }, params)
      .pipe(this.http.handleHttpError(opts.error));
  }

  ignoreFailedBenefitsVerification(
    idOrInsurance: number | ITempImprovementPatientInsurances,
    opts: Partial<IAPIFetchRequestOptions> = {}
  ): Observable<ITempImprovementPatientInsurances> {
    const params = this.http.generateFetchParams(opts);
    const id = typeof idOrInsurance === 'number' ? idOrInsurance : idOrInsurance.id;

    return this.http.post<IAPIFetchResponse<ITempImprovementPatientInsurances>>(
      `${this.fetchTempImpUrl(id)}/ignore_bv_failure`,
      {},
      params
    )
      .pipe(
        map((resp) => convertTempImprovedPatientInsurance(resp.data)),
        this.http.handleHttpError(opts.error)
      );
  }

  fetchLastBenefitInformation$(
    id: number,
    opts: Partial<IAPIListRequestOptions> = {}
  ): Observable<IBenefitsInformation> {
    if (id == null || id === undefined) {
      return of(null);
    }

    // Uncomment line below to test local json files and comment all code below it inside this method
    // return of(convertLastBenefitInformation(benefitsData.data));

    const params = this.http.generateListParams(opts);

    return this.http.get<IAPIFetchResponse<any>>(`${this.fetchTempImpUrl(id)}/last_benefit_information`, params)
      .pipe(
        map((res) => convertLastBenefitInformation(res.data)),
        this.http.handleHttpError(opts.error)
      );
  }

  get indexTempImpUrl(): string {
    return `${this.configurationService.env.svcBase}/billing/temporary_improvement/patient_insurances`;
  }

  fetchTempImpUrl(id: number | string): string {
    return `${this.indexTempImpUrl}/${id}`;
  }

  // Rails types filter[persona_client_id] as a strict Integer. A nullish or "null"
  // value causes SoberSwag to raise and the endpoint 500s. Drop the request so
  // callers with stale state (or unguarded call sites) can't page on-call.
  private sanitizePersonaClientIdFilter(
    opts: Partial<IAPIListRequestOptions>
  ): Partial<IAPIListRequestOptions> | null {
    if (!opts.filter) {
      return opts;
    }

    const filters = Array.isArray(opts.filter) ? opts.filter : [opts.filter];
    const hasInvalid = filters.some((f) => f.key === 'persona_client_id' && this.isInvalidPersonaClientIdValue(f.value));

    if (!hasInvalid) {
      return opts;
    }

    return null;
  }

  private isInvalidPersonaClientIdValue(value: unknown): boolean {
    return value == null || value === '' || value === 'null' || value === 'undefined';
  }
}
